/**
 * Gameplay guide — assembled per read, showing only what the player has unlocked.
 *
 * WHY THIS IS COMPOSED RATHER THAN ONE STRING: the guide is also the game's
 * biggest spoiler surface. A new player opening Help met the whole ladder at
 * once — prestige, a second prestige, convoy routes — which both buries the one
 * loop they can actually play and gives away the reveals the layers exist to
 * deliver. Sections are gated on the SAME one-way latches the header uses
 * (`isCommandRevealed`, `isSyndicateRevealed`), so Help can never disagree with
 * which layers the switcher is offering.
 *
 * The gates are deliberately COARSE — one per layer, not one per mechanic.
 * Hiding artifacts until the first blueprint drops, or campsites until the first
 * one is walked, would be a second progression system to maintain and would keep
 * answers from players who are looking something up mid-raid. The rule is: hide
 * a LAYER the player has not been shown, not a detail inside one they have.
 *
 * Forward references matter as much as sections. A line about "Command Marks"
 * inside the always-on crafting section leaks exactly as much as the Command
 * chapter would, so those clauses are gated individually below.
 */

import { isCommandRevealed } from './command-core.js';
import { isSyndicateRevealed } from './syndicate-core.js';

const ALWAYS = () => true;

/** The layer ladder, in order. Each entry appears once its layer is revealed. */
const LAYERS = [
    {
        when: ALWAYS,
        md: `**The Field** — run raids with your active character to earn credits, level
   skills, craft gear, and craft artifacts.`,
    },
    {
        when: isCommandRevealed,
        md: `**Command (prestige)** — when a character is established, **promote** them
   into your faction's command. They retire into your Legends gallery, you bank
   **Command Marks**, and a fresh recruit starts the next tour. Marks buy
   permanent account-wide upgrades that make every future tour faster.`,
    },
    {
        when: isSyndicateRevealed,
        md: `**Syndicate (second prestige)** — once fifteen officers have been promoted,
   **found a Syndicate** from them. Command starts over from nothing, and in
   exchange you gain **Writs**, permanent **charters**, and **Operations** — a
   planet-scale convoy economy that runs while the Field runs itself.`,
    },
];

/**
 * The opening section, sized to how much of the ladder is visible.
 *
 * A numbered list of one item reads like a list with things missing, so a
 * player who has only the Field gets prose instead — the shape itself should
 * not hint at what is being withheld.
 */
function bigPicture() {
    const open = LAYERS.filter(l => l.when());
    const intro = `You lead operatives who raid collapsed zones for loot, level them up, and gear
them out.`;

    if (open.length === 1) {
        return `## The Big Picture

${intro} Run raids with your active character to earn credits, level
skills, craft gear, and craft artifacts.`;
    }

    const ladder = open.map((l, i) => `${i + 1}. ${l.md}`).join('\n');
    const compounds = isSyndicateRevealed()
        ? `Progress compounds: each tour is quicker than the last thanks to the upgrades
your promoted Legends paid for, and each **era** is quicker than the last thanks
to the charters your Syndicate signed.`
        : `Progress compounds: each tour is quicker than the last thanks to the upgrades
your promoted Legends paid for.`;
    const currencies = isSyndicateRevealed()
        ? 'Marks live in Command, Writs in the Syndicate, credits and energy in the Field.'
        : 'Marks live in Command, credits and energy in the Field.';

    return `## The Big Picture

${intro} The game has ${open.length === 2 ? 'two' : 'three'} connected layers, each one built out of the last:

${ladder}

${compounds}

**Switching layers:** the button at the top left is the layer switcher — the
header only ever shows one layer's screens and currencies at a time. ${currencies}`;
}

const GUIDE_HEAD = `# Gameplay Guide`;

/** Always shown. Built per read so the gated clauses inside it stay live. */
const fieldBody = () => `## Characters & Factions

You can keep up to **3 characters**. Each has its own skills, level, inventory,
and gear. Faction is chosen at creation and is permanent.

### Dominion 🔧 — engineers and systems tacticians
📡 Signal Relay · 💻 System Override · 🤖 Drone Command · ⚠️ Safeguard · 🔬 Quantum Scan

### Wilders 🌿 — biotech operatives and survival engineers
🌱 Reclamation · 🌿 Growth Control · 🦎 Wild Influence · 🧬 Bio Shield · ☣️ Containment

### Veil 👤 — infiltrators and intelligence specialists
🗺️ Echo Trace · 🔓 Lock Breach · 👤 Stealth Transit · 👻 Signal Mask · 🌀 Transit Anchor

Switch characters from the Field layer's **Characters** screen → **Switch**.

---

## Skills & Leveling

Each faction has **5 skills**. Skills and character level grow by running raids —
both successes and failures grant XP, with successes granting more. Leveling up
awards **skill points** (spend them to raise individual skills) and increases max
HP. Higher skill levels raise your success chance on rooms that use that skill.

At higher levels you can also spend skill points to **unlock synergies** for a
skill, which lets its gear boost teammates' matching events during a raid.

---

## Raids

Raids are the core loop:

1. Pick a **region** and a **difficulty tier**.
2. Assemble a **team** — your character plus up to **2 contractors** to cover
   skills you're missing.
3. **Plan a path** through a procedurally generated grid to the exit.
4. **Start** — the raid runs automatically, resolving each room in turn.
5. Collect **credits, materials, blueprints, and XP**; watch for bonus events.

### Success is about survival
Before you start, each room shows a success chance — that's your odds of a
**clean run** (which yields the best loot). Failing a skill check doesn't end the
raid; it just deals **damage** scaled to the tier. What ends a raid early is a
**full team wipe** (everyone at 0 HP), which forfeits the reward. So push tiers
where your team can *survive*, not only where every check is guaranteed.

### Difficulty tiers
- **Tier 1** — smaller grids, gentle checks; where you build early levels.
- **Tier 2** — larger grids, tougher checks, better loot.
- **Tier 3** — the largest/hardest raids, and the **only source of artifact
  blueprints**.

Higher tiers use bigger grids, longer path limits, and stronger contractors.

### Room types
- **🌀 Start / 🌌 Exit** — entry and completion, no challenge.
- **🚪 Gates** — hardest skill checks, the best and most loot.
- **⚠️ Hazards** — easier checks, moderate loot.
- **🏃 Roads** — free movement, no checks and no loot; don't count toward the
  path-length limit.
- **🏕️ Campsites** — the team rests and heals (spending any field supplies you
  carry, then a base heal); no checks.

### Highlights
In the raid panel's **Highlights** tab you can mark rooms that can drop a chosen
**item**, or that use a chosen **skill**, to plan the best path at a glance.

---

## Contractors & Marketplace

- **Contractors** are AI operatives you hire to fill skill gaps. They're paid a
  percentage of the raid's credits, carry their own gear, and are temporary
  (they don't level up). The pool refreshes over time.
- **Marketplace** — contract your own character out for other players to hire,
  earning passive credits and XP while you're away.

---

## Loot, Gear & Crafting

Raids drop **credits**, faction **materials** (common → uncommon → rare → epic),
and **blueprints** (Tier 3 only). Materials feed crafting.

### Skill Gear
Craftable equipment tied to a technique. Equip **one piece per skill** to raise
that skill's success chance; some gear also enables **synergies** that help
teammates' events. Skill gear is equip-once — **one craft per character**
(a duplicate would do nothing), so a finished gear set is a clear milestone.

### Artifacts
Each character has **two artifact slots**:
- a **Skill artifact** (boosts one skill's success), and
- a **Utility artifact** (economy, defense, or utility effects).

Artifacts use a two-step model:
1. **Unlock** — blueprints drop only from Tier 3. Consume a blueprint once to
   **unlock that design for your whole account** (any character can then craft
   it).${isCommandRevealed() ? ' This is a one-time milestone worth a chunk of Command Marks.' : ''}
2. **Craft** — build the unlocked design from materials. Like gear, it's
   **one craft per character**.

### Consumables
- **Field Supplies** — restore team HP; bought with credits, used before a raid
  or automatically while resting at a campsite.
- **Technique Consumables** — crafted from materials; **auto-rig** on their
  technique's first success in a raid, then every room where that technique
  succeeds runs faster for the rest of the raid.

---

## Collapse Keys & the Archive

Nobody agrees on what ended this world, and the three factions do not agree with
each other. **Collapse Keys** are the records that bear on it — and the Archive
sets each one beside what your faction makes of it.

A successful raid can recover one. They arrive **in order**, and the tier you
raid sets how far the story can go: keys **1–2** surface at Tier 1, **3–4** at
Tier 2, **5–7** at Tier 3. So a Tier 1 character will find two and then no more,
however long they raid — the way on is deeper raids, not more of them.

**The Archive** screen appears in the Field layer with your first key and never
leaves. Every key stays there to be re-read: the fragment as it was recorded,
and what your faction makes of it.

Three things worth knowing:
- **Keys are yours, not your character's.** They are filed to your account, not
  to whoever recovered them, and cannot be sold, crafted or lost.${isCommandRevealed() ? ' No promotion or prestige ever takes one back.' : ''}
- **Each faction has its own set of seven**, recovered by raiding with a
  character of that faction. The Archive shows all three sets, including ones
  you have never played — the other factions' theories are part of the picture.
- **Seven per faction is what the field holds.** The last three of each set are
  somewhere no current expedition can reach.`;

/** Shown once the Command layer has been revealed. Built per read: its Vault
 *  paragraph names the Syndicate only when that layer is known too. */
const commandBody = () => `## Command (Prestige)

The **Command** tab appears once your active character is worth enough Command
Marks. This is the meta-game.

### Promotion
Promoting your active character:
- retires them into your **Legends** gallery (a permanent record),
- banks **Command Marks** based on their career, and
- starts the next **tour** with a fresh recruit (most credits are spent down —
  you keep a small fraction, floored near 1,000).

Promotion resets the tour, but these **persist**: Command Marks, Command
upgrades, your Legends, unlocked regions, and your other characters.

### Command Marks come from
- **Skill gear** owned,
- **Artifact designs unlocked** (the biggest single source),
- **Artifacts crafted**,
- **Tier-3 wins**, and
- **Character level**.

So a tour's goal is legible: finish your gear/artifact set, push Tier 3, and
level up — then promote at the peak.

### Command upgrades (spend Marks)
- **🩺 Field Conditioning** — recruits start with extra max HP.
- **🏛️ Officer Academy** — recruits start at a higher level.
- **🔋 Forward Depot** — energy returns faster, which is what really sets your
  raid throughput.
- **🗺️ Regional Charter** — unlock additional regions for all characters.
- **📖 Legend Bonus** — bonus XP for every enshrined Legend.
- **🛰️ Auto-Scout** — auto-generates the grid and proposes the best path.
- **📒 Foreman's Ledger** — auto-reserves the best contractors for your path.
- **🤖 Auto-Raid Protocol** — runs raids hands-off: charts, staffs, re-tools,
  launches, then chains the next while energy holds.
- **⚙️ Auto-Fabricator** — crafts hands-off between raids: unlocks artifact
  designs first, then builds gear and the components it needs.

**Buy the automation pair together.** Most of a character's Marks come from
crafting, so a character that raids by itself but never crafts earns a fraction
of a normal tour. Auto-Raid and Auto-Fabricator are the two halves of one loop —
raid → craft → marks → promote — and the Syndicate's Succession Doctrine
requires both before it will run that loop for you.

### The Vault
Promotion no longer destroys your materials. What the outgoing character was
carrying is **split**: a share goes straight to your next recruit, and the
remainder is set aside${isSyndicateRevealed() ? ' — **consigned** to the Syndicate you found next' : ''}. Both piles are
visible on Command → **Vault**. Artifact blueprints always survive in full.

Each promotion is a **Tour**; rotating characters up to command builds a
persistent account that keeps getting stronger.`;

/** Shown once a Syndicate is within reach (15 Legends) or already founded. */
const SYNDICATE_BODY = `## Syndicate Charters (Second Prestige)

The **Syndicate** layer appears once you have promoted **15 officers**. Founding
a Syndicate is the second prestige: Command goes back to zero, and what you get
in exchange outlives every era that follows.

### Founding
The founding ceremony itemises both columns before you sign, because it cannot
be undone.

**Wiped:** Command Marks, every Legend, Command upgrades, regional charters,
your characters, and credits (down to the levy floor).

**Kept:** every **Writ** and charter, unlocked artifact designs, artifact
blueprints in the vault, your Syndicate **Leader** and past leaders, cleared
Blackout perks, and the whole consigned material dock.

The era's best officer — most Marks earned, not the newest — becomes your
**Leader**, a permanent anchor who can run Operations forever.

### Writs
**📜 Writs** are the Syndicate's currency, issued at founding from everything
that era banked. The yield is damped (it scales with the *square root* of era
Marks), which is deliberate: camping one era to farm Writs loses to founding
again sooner. Growth comes from founding **more**, not from founding **bigger**.

### Charters (spend Writs)
- **📋 Standing Orders** — every founding starts the new era with Auto-Raid
  Protocol and Auto-Fabricator already running, whether or not Command bought
  them. The cheapest thing on the board and usually the first pick.
- **🕊️ Succession Doctrine** — promotes at the peak Marks rate and raises the
  successor from your template, hands-off. Requires both automation upgrades.
- **🗺️ Operations Charter** — unlocks Operations, and licenses 1 / 2 / 3
  concurrent convoys.
- **🏛️ Founder's Endowment** — each new Command era starts with banked Marks.
- **📚 Institutional Memory** — found the next Syndicate with fewer Legends.
  This is the real compression lever: it shortens every era after it.
- **📦 Provision Contracts** — operation cargo costs less.
- **🌑 Blackout Sanction** — unlocks Blackout Contracts.

### Operations
Where the Field plans a path through a room grid, the Syndicate plans a **route
across the world map** — the same decision at planetary scale.

- **A route is two capitals.** You pick the destination and the staging point;
  the ground between them sets the duration (roughly 2.5–11 hours). Distance is
  a real trade, not a menu of tiers.
- **Cargo comes from the dock** (Syndicate → **Stores**), stocked by the
  consignments your promotions banked. A dock is a per-era budget that only goes
  down, so an era's promotions provision the *next* Syndicate.
- **Destinations pay differently.** Every capital shows its own **demand
  premium** for the signature you are shipping, and delivering into a region
  cools its appetite for that faction's goods for a while. The map is a decision
  you re-make, not a table you solve once.
- **Convoys run in real time** and settle while you are away — one round per
  licensed slot. Deliveries above a high premium also trickle **Writs** back.
- Each convoy is led by your Leader or an officer promoted **this era**, so
  mid-era promotions have a second payoff.

### Blackout Contracts
Optional raids under a hard modifier — no grid preview, no consumables, no
contractors, and so on — each paying one **permanent** account perk. You clear a
contract by *finishing* such a raid; failing it changes nothing. Automation
refuses to run them: the difficulty has to be a choice you made.

### Hands-off by design
With Standing Orders, Succession Doctrine and the two automation upgrades in
place, the whole Command loop runs itself — raid, craft, promote, raise a
successor — while you spend your attention on the Syndicate's routes. That is
what the layer is for: your old game becomes the engine for the new one.`;

/** Always shown; two of its lines only make sense once Command exists. */
const tail = () => `## Energy, HP & Credits

- **Energy** — raids cost energy based on the path; it regenerates over time, and
  you can buy more with credits from the header.
- **HP** — lost to failed skill checks, restored at campsites (field supplies +
  base heal) and slowly over time outside raids. A full team wipe ends a raid.
- **Credits** — earned from raids, spent on contractors, consumables, energy, and
  crafting.${isCommandRevealed() ? ' Largely reset each promotion (the end-of-tour spend-down is the game).' : ''}

---

## Tips

- **Early** — run Tier 1, level up, and start crafting your skill-gear set.
- **Mid** — move to Tier 2, unlock synergies, and begin working toward artifacts.
- **Late** — push Tier 3 for blueprints and complete your gear/artifact set${isCommandRevealed() ? `, then
  **promote** to bank Marks and buy upgrades that speed up the next tour` : ''}.${isSyndicateRevealed() ? `
- **Prestige** — once fifteen officers are promoted, found a Syndicate. Take
  **Standing Orders** first (it is the cheapest charter and it keeps your
  automation across every future founding), then **Institutional Memory**, which
  shortens every era after it.` : ''}
- Match your raid tier to what your team can **survive**, not just to guaranteed
  checks — a clean Tier 2 beats a wipe on Tier 3.${isCommandRevealed() ? `
- Buy **Forward Depot** earlier than it looks worth: energy regeneration, not
  raid speed, is what caps how much you can actually run in a day.` : ''}

---

## FAQ

**Can I change my faction?** No — it's permanent. Roll another character to try a
different faction.

**How many artifacts can I equip?** Two — one Skill artifact and one Utility
artifact.

**Why can't I craft a second copy of gear/an artifact?** Durable gear and
artifacts are equip-once, so each is capped at one craft per character. Finishing
the set is the point.

**Do contractors level up?** No — they're temporary hires.

**What happens if my team wipes?** The raid ends early with no reward, but your
character returns safely — characters can't permanently die.${isCommandRevealed() ? `

**What do I lose when I promote?** The character (they become a Legend) and most
of your credits — their leftover relics and consumables are sold off first, so
the amount you keep is a share of that larger total. Their materials are not
destroyed: they are split and set aside. You keep Marks, upgrades, unlocked
regions, and every other character — and gain a permanent power boost.` : ''}${isSyndicateRevealed() ? `

**When should I found a Syndicate?** As soon as you can. Writs scale with the
square root of an era's Marks, so a longer era pays steadily less per hour than
founding again and starting the next one with better charters.

**Do I lose my Legends when I found?** Yes — the whole gallery is spent to
create the Syndicate, and the era's best officer becomes its permanent Leader.
Unlocked artifact designs, blueprints, Writs, charters and the material dock all
carry over.

**Why won't Succession Doctrine let me buy it?** It needs **both** Auto-Raid
Protocol and Auto-Fabricator. It promotes on your Marks *rate*, and most Marks
come from crafting — automating the raids alone would have it judging a
character that only earns a fraction of a normal tour.

**Why is Auto-Craft sitting on "Idle"?** It is on, but has nothing it can build
right now: no blueprint to unlock, no complete recipe, and nothing affordable.
It will start again on its own as raids bring materials in.` : ''}

---

*Last Updated: 8/5/2026*`;

/**
 * The guide the player should see right now.
 *
 * Assembled per call rather than cached: the Command reveal fires mid-session on
 * a background tick, so a guide built once at boot would still be hiding a layer
 * the header is already showing.
 */
export function buildGameplayGuide() {
    return [
        GUIDE_HEAD,
        bigPicture(),
        fieldBody(),
        isCommandRevealed() ? commandBody() : null,
        isSyndicateRevealed() ? SYNDICATE_BODY : null,
        tail(),
    ].filter(Boolean).join('\n\n---\n\n');
}
